import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, KeyRound, Building2, Save, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/apiClient";

export default function Settings() {
  const [tab, setTab] = useState("business");

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <span className="text-[11px] tracking-[0.22em] uppercase text-blue-400 font-medium">
          Configuration
        </span>
        <h1 className="font-display text-3xl md:text-4xl mt-2">Settings</h1>
      </div>

      <div className="flex gap-2 border-b border-white/10">
        <TabButton active={tab === "business"} onClick={() => setTab("business")} testid="tab-business">
          <Building2 size={13} className="mr-1.5" /> Business info
        </TabButton>
        <TabButton active={tab === "password"} onClick={() => setTab("password")} testid="tab-password">
          <KeyRound size={13} className="mr-1.5" /> Password
        </TabButton>
      </div>

      {tab === "business" ? <BusinessSettings /> : <PasswordSettings />}
    </div>
  );
}

function TabButton({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${
        active ? "border-blue-500 text-white" : "border-transparent text-white/50 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function BusinessSettings() {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/settings");
        setForm(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      }
    })();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));
  const save = async () => {
    setBusy(true);
    try {
      const payload = { ...form, default_deal_value: Number(form.default_deal_value) || 0 };
      await api.patch("/admin/settings", payload);
      toast.success("Settings saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!form) {
    return <div className="text-white/50 text-sm">Loading…</div>;
  }

  return (
    <div className="card-soft rounded-2xl p-6 max-w-2xl" data-testid="business-settings-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <F l="Business name"><Input value={form.business_name || ""} onChange={set("business_name")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
        <F l="Business email"><Input value={form.business_email || ""} onChange={set("business_email")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
        <F l="Business phone"><Input value={form.business_phone || ""} onChange={set("business_phone")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
        <F l="Default deal value ($)"><Input type="number" min={0} value={form.default_deal_value ?? 0} onChange={set("default_deal_value")} className="bg-[#0A0A0A] border-white/15 text-white h-10" /></F>
      </div>
      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={busy} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full h-10 px-5" data-testid="save-business-settings">
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Save size={14} className="mr-1.5" />}
          Save
        </Button>
      </div>
    </div>
  );
}

function PasswordSettings() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New password and confirmation don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/admin/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated.");
      setCur(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-soft rounded-2xl p-6 max-w-lg space-y-4" data-testid="password-settings-form">
      <div className="flex items-start gap-3 p-3 rounded-lg border border-amber-500/30 bg-amber-500/5">
        <ShieldAlert size={16} className="text-amber-300 mt-0.5" />
        <p className="text-xs text-amber-100/80 leading-relaxed">
          Choose a strong password. This is the only credential to your CRM — 5 failed login attempts trigger a 30-minute lockout.
        </p>
      </div>
      <F l="Current password">
        <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="bg-[#0A0A0A] border-white/15 text-white h-10" data-testid="password-current" />
      </F>
      <F l="New password (min 8 chars)">
        <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="bg-[#0A0A0A] border-white/15 text-white h-10" data-testid="password-new" />
      </F>
      <F l="Confirm new password">
        <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="bg-[#0A0A0A] border-white/15 text-white h-10" data-testid="password-confirm" />
      </F>
      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={busy || !cur || !next} className="bg-blue-500 hover:bg-blue-600 text-white rounded-full h-10 px-5" data-testid="password-save">
          {busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <KeyRound size={14} className="mr-1.5" />}
          Update password
        </Button>
      </div>
    </form>
  );
}

function F({ l, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.18em] text-white/55">{l}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
